import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Card,
  Row,
  Col,
  Container,
  Modal,
  Button,
  Form,
  Spinner,
} from "react-bootstrap";
import Swal from "sweetalert2";

const Doctors = () => {
  const [loading, setLoading] = useState(false);

  const [doctors, setDoctors] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [doctorData, setDoctorData] = useState({
    id: null,
    name: "",
    email: "",
    contact: "",
    password: "", // Added password
    department_id: "",
    description: "",
    profile_pic: null,
  });
  const [isUpdate, setIsUpdate] = useState(false);

  // Use the env URL for the API endpoint
  const apiUrl = process.env.REACT_APP_API_URL;

  useEffect(() => {
    // Fetch all doctors
    axios
      .get(`${apiUrl}/get-doctors`)
      .then((response) => {
        setDoctors(response.data.doctors);
      })
      .catch((error) => console.error("Error fetching doctors:", error));

    // Fetch departments to map names to IDs
    axios
      .get(`${apiUrl}/get-categories`)
      .then((response) => {
        setDepartments(response.data.categories); // Assuming categories contain the department names and ids
      })
      .catch((error) => {
        Swal.fire("Error", "Failed to fetch departments", "error");
      });
  }, [apiUrl]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setDoctorData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    setDoctorData({
      ...doctorData,
      profile_pic: e.target.files[0],
    });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setDoctorData({
      id: null,
      name: "",
      email: "",
      contact: "",
      password: "", // Reset password field
      department_id: "",
      description: "",
      profile_pic: null,
    });
    setIsUpdate(false);
  };

  const handleAddDoctor = () => {
    const formData = new FormData();
    formData.append("name", doctorData.name);
    formData.append("email", doctorData.email);
    formData.append("contact", doctorData.contact);
    formData.append("password", doctorData.password); // Include password
    formData.append("department_id", doctorData.department_id);
    formData.append("description", doctorData.description);

    if (doctorData.profile_pic) {
      formData.append("profile_pic", doctorData.profile_pic);
    }

    setLoading(true);

    axios
      .post(`${apiUrl}/add-doctor`, formData, {
        headers: {
          "Content-Type": "multipart/form-data", // Important for file uploads
        },
      })
      .then((response) => {
        Swal.fire("Success", response.data.message, "success");

        const newDoctor = {
          ...doctorData,
          id: response.data.doctorId,
          profile_pic: response.data.profile_pic,
        };

        setDoctors((prevDoctors) => [...prevDoctors, newDoctor]);

        handleCloseModal();
      })
      .catch((error) => {
        Swal.fire("Error", error.response.data.message, "error");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleUpdateDoctor = () => {
    const formData = new FormData();
    formData.append("name", doctorData.name);
    formData.append("email", doctorData.email);
    formData.append("contact", doctorData.contact);
    formData.append("password", doctorData.password); // Include password
    formData.append("department_id", doctorData.department_id);
    formData.append("description", doctorData.description);

    if (doctorData.profile_pic instanceof File) {
      formData.append("profile_pic", doctorData.profile_pic);
    }

    axios
      .put(`${apiUrl}/update-doctor/${doctorData.id}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response) => {
        Swal.fire("Updated", response.data.message, "success");

        // Update local state
        const updatedDoctor = response.data.doctor;

        setDoctors((prevDoctors) =>
          prevDoctors.map((doc) =>
            doc.id === updatedDoctor.id ? updatedDoctor : doc
          )
        );

        handleCloseModal();
      })
      .catch((error) => {
        console.error(error);
        Swal.fire(
          "Error",
          error.response?.data?.message || "Update failed",
          "error"
        );
      });
  };

  const handleShowAddModal = () => {
    setIsUpdate(false);
    setShowModal(true);
  };

  const handleShowUpdateModal = (doctor) => {
    setIsUpdate(true);
    setDoctorData(doctor);
    setShowModal(true);
  };

  return (
    <div style={{ backgroundColor: "aliceblue" }}>
      <Button
        variant="primary"
        onClick={handleShowAddModal}
        className="fw-bold"
      >
        Add Doctor
      </Button>
      <Container className="mt-4">
        <Row xs={1} md={2} lg={4} className="g-4">
          {doctors.map((doctor) => (
            <Col key={doctor.id}>
              <Card className="h-100 shadow-sm m-auto">
                <Card.Img
                  variant="top"
                  src={
                    doctor.profile_pic
                      ? `${apiUrl}/${doctor.profile_pic}`
                      : "https://via.placeholder.com/150"
                  }
                  alt="Profile"
                  style={{
                    height: "250px",
                    objectFit: "contain",
                  }}
                />
                <Card.Body>
                  <Card.Title>{doctor.name}</Card.Title>
                  <Card.Subtitle className="mb-2 text-muted">
                    {departments.find(
                      (dept) => dept.id === parseInt(doctor.department_id)
                    )?.name || "N/A"}
                  </Card.Subtitle>
                  <Card.Text>
                    <strong>Email:</strong> {doctor.email}
                    <br />
                    <strong>Contact:</strong> {doctor.contact}
                    <br />
                    <strong>Description:</strong> {doctor.description}
                  </Card.Text>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleShowUpdateModal(doctor)}
                    className="fw-bold"
                  >
                    Edit Details
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </Container>

      {/* Modal for adding/updating doctor */}
      <Modal
        show={showModal}
        onHide={handleCloseModal}
        className="mt-5"
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>{isUpdate ? "Update Doctor" : "Add Doctor"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col sm={6}>
                <Form.Group controlId="doctorName">
                  <Form.Label className="fw-bold">Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={doctorData.name}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col sm={6}>
                <Form.Group controlId="doctorEmail">
                  <Form.Label className="fw-bold mt-2">Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={doctorData.email}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col sm={6}>
                <Form.Group controlId="doctorContact">
                  <Form.Label className="fw-bold mt-2">Contact</Form.Label>
                  <Form.Control
                    type="text"
                    name="contact"
                    value={doctorData.contact}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col sm={6}>
                <Form.Group controlId="doctorPassword">
                  <Form.Label className="fw-bold mt-2">Password</Form.Label>
                  <Form.Control
                    type="password"
                    name="password"
                    value={doctorData.password}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group controlId="doctorDepartment">
              <Form.Label className="fw-bold mt-2">Department</Form.Label>
              <Form.Control
                as="select"
                name="department_id"
                value={doctorData.department_id}
                onChange={handleChange}
                required
              >
                <option value="">Select Department</option>
                {departments.map((dept) => (
                  <option key={dept.id} value={dept.id}>
                    {dept.name}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>

            <Form.Group controlId="doctorDescription">
              <Form.Label className="fw-bold mt-2">Description</Form.Label>
              <Form.Control
                type="text"
                name="description"
                value={doctorData.description}
                onChange={handleChange}
              />
            </Form.Group>

            <Form.Group controlId="doctorProfilePic">
              <Form.Label className="fw-bold mt-2">Profile Picture</Form.Label>
              <Form.Control
                type="file"
                name="profile_pic"
                onChange={handleFileChange}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            onClick={isUpdate ? handleUpdateDoctor : handleAddDoctor}
            className="fw-bold mt-2"
            disabled={loading} // Disable button when loading
          >
            {loading ? (
              <div className="d-flex justify-content-center">
                <Spinner animation="border" size="sm" />
              </div>
            ) : (
              <span>{isUpdate ? "Update" : "Add"} Doctor</span>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Doctors;
